import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, RotateCcw, Lock, Sparkles, Wand2 } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { libApi, matchApi, apiError } from "@/lib/api";
import { MATCHER } from "@/constants/testIds";
import questionnaire from "@/data/prompt-matcher-questionnaire.json";

const STORAGE_KEY = "promptivo.matcher.answers";
const ALL = "all";

const GOAL_Q = questionnaire.questions.find((q) => q.id === "goal");
const INPUT_Q = questionnaire.questions.find((q) => q.id === "inputType");

function OptionButton({ selected, onClick, testId, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-pressed={selected}
      className={`flex w-full items-center justify-between gap-3 rounded-field border px-5 py-4 text-left text-[15px] transition-colors duration-200 ${
        selected
          ? "border-brand-accent bg-brand-accent/10 text-brand-text"
          : "border-brand-line bg-brand-surface text-brand-sec hover:border-brand-accent/50 hover:text-brand-text"
      }`}
    >
      <span>{children}</span>
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
          selected ? "border-brand-accent bg-brand-accent text-brand-on" : "border-brand-line"
        }`}
      >
        {selected && <span className="h-2 w-2 rounded-full bg-current" />}
      </span>
    </button>
  );
}

export default function PromptMatcher() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefillPkg = params.get("pkg");
  const headingRef = useRef(null);

  const [packages, setPackages] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  });
  const [results, setResults] = useState(null); // null=quiz, []=submitted
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    libApi
      .publicPackages()
      .then((d) => setPackages(d.packages || []))
      .catch(() => setPackages([]));
  }, []);

  useEffect(() => {
    if (prefillPkg && !answers.package) {
      setAnswers((a) => ({ ...a, package: prefillPkg }));
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillPkg]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
  }, [answers]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [step, results]);

  const packageOptions = useMemo(
    () => [{ slug: ALL, name: "Wszystkie pakiety" }, ...packages],
    [packages],
  );

  const steps = [
    { id: "package", label: "W jakiej branży działasz?", options: packageOptions.map((p) => ({ value: p.slug, label: p.name })) },
    { id: "goal", label: GOAL_Q.label, options: GOAL_Q.options.map((o) => ({ value: o, label: o })) },
    { id: "inputType", label: INPUT_Q.label, options: INPUT_Q.options.map((o) => ({ value: o, label: o })) },
  ];

  const current = steps[step];
  const currentValue = answers[current?.id];

  const select = (value) => {
    setAnswers((a) => ({ ...a, [current.id]: value }));
    if (step < steps.length - 1) {
      setTimeout(() => setStep((s) => s + 1), 120);
    }
  };

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await matchApi.match({
        package_slug: answers.package,
        goal: answers.goal,
        input_type: answers.inputType,
      });
      setResults(data.results || []);
    } catch (err) {
      setError(apiError(err.response?.data?.detail, err.message));
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setAnswers({});
    setResults(null);
    setStep(0);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const canSubmit = answers.package && answers.goal && answers.inputType;

  return (
    <AppShell>
      <div data-testid={MATCHER.root} className="mx-auto max-w-3xl">
        <header className="mb-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-accent/40 px-3 py-1 text-xs font-medium uppercase tracking-wide text-brand-accent">
            <Wand2 size={14} /> Dopasuj prompt
          </span>
          <h1 className="mt-4 font-display text-3xl font-semibold text-brand-text sm:text-4xl">
            Znajdźmy prompt dla Twojego celu
          </h1>
          <p className="mt-3 text-brand-sec">
            Odpowiedz na 3 krótkie pytania — pokażemy maksymalnie 3 najlepiej dopasowane prompty z katalogu.
          </p>
        </header>

        {results === null ? (
          <section className="rounded-panel border border-brand-line bg-brand-surface p-6 sm:p-8">
            <div className="mb-6 flex items-center gap-2">
              {steps.map((s, i) => (
                <span
                  key={s.id}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-brand-accent" : "bg-brand-line"}`}
                />
              ))}
            </div>
            <p className="mb-1 text-sm text-brand-muted">Krok {step + 1} z {steps.length}</p>
            <h2
              ref={headingRef}
              tabIndex={-1}
              data-testid={MATCHER.question}
              className="font-display text-xl font-semibold text-brand-text outline-none"
            >
              {current.label}
            </h2>

            <div className="mt-5 space-y-3">
              {current.options.map((o) => (
                <OptionButton
                  key={o.value}
                  testId={MATCHER.option}
                  selected={currentValue === o.value}
                  onClick={() => select(o.value)}
                >
                  {o.label}
                </OptionButton>
              ))}
            </div>

            {error && (
              <p className="mt-4 rounded-field border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {error}
              </p>
            )}

            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                data-testid={MATCHER.backButton}
                disabled={step === 0}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm text-brand-sec transition-colors hover:text-brand-text disabled:opacity-40"
              >
                <ArrowLeft size={16} /> Wstecz
              </button>
              {step === steps.length - 1 ? (
                <button
                  type="button"
                  data-testid="matcher-submit-button"
                  disabled={!canSubmit || loading}
                  onClick={submit}
                  className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-6 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-50"
                >
                  {loading ? "Dopasowuję…" : "Pokaż dopasowania"} <Sparkles size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  disabled={!currentValue}
                  onClick={() => setStep((s) => s + 1)}
                  className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-6 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-50"
                >
                  Dalej <ArrowRight size={16} />
                </button>
              )}
            </div>
          </section>
        ) : (
          <section data-testid={MATCHER.results}>
            <div className="mb-5 flex items-center justify-between gap-3">
              <h2 ref={headingRef} tabIndex={-1} className="font-display text-xl font-semibold text-brand-text outline-none">
                {results.length ? "Twoje dopasowania" : "Brak dopasowań"}
              </h2>
              <button
                type="button"
                data-testid={MATCHER.restartButton}
                onClick={restart}
                className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2 text-sm text-brand-sec transition-colors hover:text-brand-text"
              >
                <RotateCcw size={15} /> Zacznij od nowa
              </button>
            </div>

            {results.length === 0 ? (
              <div data-testid={MATCHER.noMatch} className="rounded-panel border border-brand-line bg-brand-surface p-8">
                <p className="text-brand-sec">
                  Nie znaleźliśmy dopasowania dla tych odpowiedzi. Przejrzyj pełny katalog albo zacznij quiz od nowa.
                </p>
                <Link
                  to="/biblioteka"
                  data-testid={MATCHER.seeOthersButton}
                  className="mt-4 inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on"
                >
                  Przejdź do biblioteki <ArrowRight size={16} />
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {results.map((r) => (
                  <article
                    key={`${r.package_slug}-${r.slug}`}
                    data-testid={MATCHER.resultCard}
                    className="rounded-panel border border-brand-line bg-brand-surface p-6"
                  >
                    <div className="flex items-center gap-2 text-xs text-brand-muted">
                      <span className="rounded-full bg-brand-elevated px-2.5 py-1">{r.package_name}</span>
                      {r.primary_tool && !r.locked && (
                        <span className="rounded-full bg-brand-elevated px-2.5 py-1">{r.primary_tool}</span>
                      )}
                      {r.locked && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-brand-elevated px-2.5 py-1 text-brand-sec">
                          <Lock size={12} /> Pakiet zablokowany
                        </span>
                      )}
                    </div>
                    <h3 className="mt-3 font-display text-lg font-semibold text-brand-text">{r.name}</h3>
                    {r.short_benefit && <p className="mt-1.5 text-sm text-brand-sec">{r.short_benefit}</p>}
                    {r.why_matched && (
                      <p className="mt-3 rounded-field bg-brand-elevated px-3 py-2 text-xs text-brand-sec">
                        <span className="font-medium text-brand-accent">Dlaczego ten prompt? </span>
                        {r.why_matched}
                      </p>
                    )}
                    <div className="mt-4">
                      {r.locked ? (
                        <Link
                          to={`/biblioteka/${r.package_slug}`}
                          data-testid={MATCHER.seePackageButton}
                          className="inline-flex items-center gap-2 rounded-button border border-brand-accent/50 px-5 py-2.5 text-sm font-semibold text-brand-accent transition-colors hover:bg-brand-accent/10"
                        >
                          Zobacz pakiet <ArrowRight size={15} />
                        </Link>
                      ) : (
                        <Link
                          to={`/prompt/${r.slug}`}
                          data-testid={MATCHER.openPromptButton}
                          className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
                        >
                          Otwórz prompt <ArrowRight size={15} />
                        </Link>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </AppShell>
  );
}
